import type { TFunction } from 'i18next';

import {
    type PenWarning,
    type PenWarningTrigger,
    WARNING_DIGITS,
    WARNING_MARGIN_DIGITS,
} from '@/domain/feedingDashboard';
import type { Locale } from '@/utils/localeFormatters';

import type { SystemLabels } from '../systemLabel';

import { format, formatSetting, formatTarget } from './penComparisonFormat';

// Full keys rather than assembled ones, so the i18n scanner can see every key in use.
const CONDITION_KEYS = {
    fcr: {
        warning: 'feedingModule.table.warning.fcrAbove',
        critical: 'feedingModule.table.warning.fcrAboveCritical',
    },
    rgi: {
        warning: 'feedingModule.table.warning.rgiBelow',
        critical: 'feedingModule.table.warning.rgiBelowCritical',
    },
} as const;

/** The column header each condition is about, so the text names what the reader sees. */
function conditionLabel(trigger: PenWarningTrigger, system: SystemLabels, t: TFunction): string {
    const labels: Record<PenWarningTrigger, string> = {
        fcrSystem: system.fcr,
        fcrBioscope: t('feedingModule.table.fcrBioscope'),
        rgiSystem: system.rgi,
        rgiBioscope: t('feedingModule.table.rgiBioscope'),
    };
    return labels[trigger];
}

/**
 * One sentence per condition met, such as "bFCR Mercatus 1.25 is above the target 1.12",
 * with the value at the precision its cell shows, and the target and margin at the
 * precision the rule compared them, so the sentence never contradicts the warning.
 */
export function warningConditions(
    warnings: PenWarning[],
    system: SystemLabels,
    t: TFunction,
    locale: Locale
): string[] {
    return warnings.map((warning) => {
        const digits = WARNING_DIGITS[warning.metric];
        return t(CONDITION_KEYS[warning.metric][warning.tier], {
            label: conditionLabel(warning.trigger, system, t),
            value: format(warning.value, digits, locale),
            target: formatTarget(warning.target, warning.metric, locale),
            margin: formatSetting(warning.warnPct, WARNING_MARGIN_DIGITS, locale),
        });
    });
}
